import type { AppointmentSubmission } from '../models/appointment-submission';
import type { BookPortalAppointmentRequest } from '../models/book-portal-appointment-request';
import type { PortalAppointment } from '../models/portal-appointment';
import type { PortalCaseDetail } from '../models/portal-case-detail';
import type { PortalCaseListItem } from '../models/portal-case-list-item';
import type { PortalDocument } from '../models/portal-document';
import type { PortalMessage } from '../models/portal-message';
import type { PortalNotifications } from '../models/portal-notifications';
import type { PushKey } from '../models/push-key';
import type { SavePushSubscription } from '../models/save-push-subscription';
import type { PortalProfile } from '../models/portal-profile';
import type { PortalSignatureRequest } from '../models/portal-signature-request';
import type { PortalSigningPackage } from '../models/portal-signing-package';
import type { PortalThread } from '../models/portal-thread';
import { apiGet, apiGetBlob, apiPost, apiPostBlob, apiPut } from './api-client';

/**
 * The customer portal's routes. Every one of them is authenticated, and **none takes a customer
 * identifier** — the server resolves the caller from the token, so there is nothing here for a
 * page to get wrong.
 */
export async function getPortalProfile(signal?: AbortSignal): Promise<PortalProfile> {
    return apiGet<PortalProfile>('/portal/me', { authenticated: true, signal });
}

/** Records the language for messages composed outside a request — an invitation, a reminder. */
export async function setPortalLanguage(language: string): Promise<void> {
    return apiPut<void>('/portal/me/language', { authenticated: true, body: { language } });
}

/** Records the customer's own IIN. Once: the server refuses a different number after the first. */
export async function setPortalIin(iin: string): Promise<void> {
    return apiPut<void>('/portal/me/iin', { authenticated: true, body: { iin } });
}

export async function getPortalCases(signal?: AbortSignal): Promise<PortalCaseListItem[]> {
    return apiGet<PortalCaseListItem[]>('/portal/cases', { authenticated: true, signal });
}

export async function getPortalCase(clientCaseId: string, signal?: AbortSignal): Promise<PortalCaseDetail> {
    return apiGet<PortalCaseDetail>(`/portal/cases/${clientCaseId}`, { authenticated: true, signal });
}

/**
 * The customer's case as a page. The body carries the wording the portal renders, in the language it
 * renders it in; the server writes every name and date on it from the customer's own view of the case.
 */
export async function generatePortalCasePdf(
    clientCaseId: string,
    language: string,
    labels: Record<string, string>
): Promise<{ blob: Blob; fileName: string | null }> {
    return apiPostBlob(`/portal/cases/${clientCaseId}/pdf`, {
        authenticated: true,
        body: { language, labels }
    });
}

export async function getPortalDocuments(
    clientCaseId: string,
    signal?: AbortSignal
): Promise<PortalDocument[]> {
    return apiGet<PortalDocument[]>(`/portal/cases/${clientCaseId}/documents`, {
        authenticated: true,
        signal
    });
}

/**
 * Unsharing withdraws this immediately — the server checks that the document is still shared on
 * every request, so a bookmarked link stops working rather than merely disappearing from the list.
 */
export async function getPortalDocumentContent(documentId: string, versionId: string): Promise<Blob> {
    return apiGetBlob(`/portal/documents/${documentId}/versions/${versionId}/content`, {
        authenticated: true
    });
}

export async function getPortalAppointments(signal?: AbortSignal): Promise<PortalAppointment[]> {
    return apiGet<PortalAppointment[]>('/portal/appointments', { authenticated: true, signal });
}

/**
 * The booking form sent as the signed-in customer. It carries no contact details: the server takes
 * them from the account and files the request under it, so it appears in the portal.
 */
export async function bookPortalAppointment(request: BookPortalAppointmentRequest): Promise<AppointmentSubmission> {
    return apiPost<AppointmentSubmission>('/portal/appointments', { authenticated: true, body: request });
}

/** Calls off one of the customer's own consultations. Offered only where `canCancel` says so. */
export async function cancelPortalAppointment(appointmentId: string, reason: string): Promise<void> {
    return apiPost<void>(`/portal/appointments/${appointmentId}/cancel`, {
        authenticated: true,
        body: { reason }
    });
}

/**
 * Reading the thread **marks the staff replies read** on the server. The count it returns is what
 * was unread when it was opened, which is why it is worth showing after the fact.
 */
/** The newest messages, or with `before` the ones written before that message. */
export async function getPortalMessages(before: string | null = null, signal?: AbortSignal): Promise<PortalThread> {
    const query = before === null ? '' : `?${new URLSearchParams({ before }).toString()}`;

    return apiGet<PortalThread>(`/portal/messages${query}`, { authenticated: true, signal });
}

export async function postPortalMessage(body: string, clientCaseId: string | null): Promise<PortalMessage> {
    return apiPost<PortalMessage>('/portal/messages', {
        authenticated: true,
        body: { clientCaseId, body }
    });
}

/** What the customer has been asked to sign, across their own cases. Drafts are not in it. */
export async function getPortalSignatureRequests(
    signal?: AbortSignal
): Promise<PortalSignatureRequest[]> {
    return apiGet<PortalSignatureRequest[]>('/portal/signature-requests', {
        authenticated: true,
        signal
    });
}

/**
 * A document in a package the customer is asked to sign. Answered for as long as the request is
 * open, whether or not the document was ever shared — being asked to sign it is the grant.
 */
export async function getPortalSigningDocumentContent(signatureRequestId: string, versionId: string): Promise<Blob> {
    return apiGetBlob(`/portal/signature-requests/${signatureRequestId}/documents/${versionId}/content`, {
        authenticated: true
    });
}

/**
 * Signs the request with a signature drawn on the pad.
 *
 * **This is the signature, not a copy of one.** It is made in the customer's own session against the
 * package the office froze, so the request shows as signed as soon as this succeeds.
 */
export async function submitPortalSignature(signatureRequestId: string, strokes: number[][][]): Promise<void> {
    return apiPost<void>(`/portal/signature-requests/${signatureRequestId}/signatures`, {
        authenticated: true,
        body: { strokes }
    });
}

/** The value to hand NCALayer. Asked for at the moment the customer presses the button, not with the list. */
export async function getPortalSigningPackage(signatureRequestId: string): Promise<PortalSigningPackage> {
    return apiGet<PortalSigningPackage>(`/portal/signature-requests/${signatureRequestId}/package`, {
        authenticated: true
    });
}

/**
 * Returns the CMS NCALayer produced. The server verifies the certificate, checks its IIN against the
 * customer's own and proves the signature covers the frozen package before the request shows signed.
 */
export async function submitPortalEcpSignature(signatureRequestId: string, cms: string): Promise<void> {
    return apiPost<void>(`/portal/signature-requests/${signatureRequestId}/ecp-signatures`, {
        authenticated: true,
        body: { cms }
    });
}

export async function getPortalNotifications(signal?: AbortSignal): Promise<PortalNotifications> {
    return apiGet<PortalNotifications>('/portal/notifications', { authenticated: true, signal });
}

/** An empty list means every unread notification — the panel's "mark all read". */
export async function markNotificationsRead(notificationIds: string[]): Promise<void> {
    return apiPost<void>('/portal/notifications/read', {
        authenticated: true,
        body: { notificationIds }
    });
}

/** The application server's public key, or null where this deployment has no key pair. */
export async function getPushKey(signal?: AbortSignal): Promise<PushKey> {
    return apiGet<PushKey>('/portal/notifications/push-key', { authenticated: true, signal });
}

export async function savePushSubscription(subscription: SavePushSubscription): Promise<void> {
    return apiPost<void>('/portal/notifications/push-subscriptions', {
        authenticated: true,
        body: subscription
    });
}

/** Sent as a body rather than a query string: an endpoint is a URL, and URLs inside URLs end up in logs. */
export async function deletePushSubscription(endpoint: string): Promise<void> {
    return apiPost<void>('/portal/notifications/push-subscriptions/delete', {
        authenticated: true,
        body: { endpoint }
    });
}
